import { Fragment, useEffect, useRef, useState, type ComponentType, type CSSProperties } from 'react';
import { ImageOff, Link, Play } from 'lucide-react';
import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { openDocViewer, openMediaCollection } from '../../../../lib/doc-viewer-presenter';
import { isSafeUrl } from '../../../../lib/safe-url';
import { socialPostLook, type SocialPostLook } from '../../../../lib/social-post-look';
import {
  postCaptionDirection,
  postExpiryNote,
  postGridShowsNotShown,
  postScheduledLine,
  postShowsNotShown,
} from '../../../../lib/social-post-look-parts';
import { AVATAR_PALETTE } from '../../../../lib/avatar-color';
import {
  socialPostCaptionIsLong,
  socialPostCaptionSegments,
  socialPostHasLogo,
  socialPostHeader,
  socialPostLinkHost,
  socialPostLogoKey,
  socialPostMediaLayout,
  socialPostMediaName,
  socialPostGridRendition,
  socialPostImageRendition,
  socialPostSingleFrame,
  SOCIAL_POST_RENDITIONS,
  type SocialPostRendition,
  socialPostVideoAspect,
  socialPostVideoSrc,
} from '../../../../lib/social-post-preview-card';
import { Avatar, Card, SocialLogo, TextButton } from '../../ui';
import { FacebookFeedPost, FacebookReel, FacebookStory } from './FacebookPostPreview';
import { GbpPostPreview } from './GbpPostPreview';
import { InstagramFeedPost, InstagramReel, InstagramStory } from './InstagramPostPreview';
import { LinkedinPostPreview } from './LinkedinPostPreview';
import { PinterestPostPreview } from './PinterestPostPreview';
import { PostControlContext, PostSubtitleParts, type PostControl, type PostSubtitleClasses, type SocialPostLookProps } from './SocialPostLookParts';
import { TiktokPhoto, TiktokVideo } from './TiktokPostPreview';
import { XPostPreview } from './XPostPreview';
import { compactPostPreview } from '../../../../lib/task-row-post-preview';
import compactStyles from './SocialPostPreviewCompact.module.css';
import { YoutubeShort, YoutubeVideo } from './YoutubePostPreview';
import styles from './SocialPostPreviewCard.module.css';

const GRID_CLASS: Record<number, string> = { 2: styles.two, 3: styles.three, 4: styles.four };

interface LookSpec {
  body: ComponentType<SocialPostLookProps>;
  card?: string;
  frame?: string;
  portrait?: string;
  timingWord: boolean;
  grid: boolean;
}

const GENERIC: LookSpec = { body: () => null, timingWord: false, grid: true };

const LOOKS: Record<SocialPostLook, LookSpec> = {
  'facebook-feed': { body: FacebookFeedPost, card: styles.facebookFeed, portrait: styles.facebookFeedPortrait, timingWord: true, grid: true },
  'facebook-reel': { body: FacebookReel, frame: styles.reelFrame, timingWord: false, grid: false },
  'facebook-story': { body: FacebookStory, frame: styles.storyFrame, timingWord: false, grid: false },
  'instagram-feed': { body: InstagramFeedPost, card: styles.instagramFeed, timingWord: true, grid: false },
  'instagram-reel': { body: InstagramReel, frame: styles.reelFrame, timingWord: false, grid: false },
  'instagram-story': { body: InstagramStory, frame: styles.storyFrame, timingWord: false, grid: false },
  linkedin: { body: LinkedinPostPreview, card: styles.linkedin, timingWord: true, grid: true },
  x: { body: XPostPreview, card: styles.x, timingWord: true, grid: true },
  'tiktok-video': { body: TiktokVideo, frame: styles.storyFrame, timingWord: false, grid: false },
  'tiktok-photo': { body: TiktokPhoto, frame: styles.storyFrame, timingWord: false, grid: false },
  'youtube-video': { body: YoutubeVideo, card: styles.youtubeVideo, timingWord: false, grid: false },
  'youtube-short': { body: YoutubeShort, frame: styles.reelFrame, timingWord: false, grid: false },
  pinterest: { body: PinterestPostPreview, card: styles.pinterest, timingWord: false, grid: true },
  gbp: { body: GbpPostPreview, card: styles.gbp, timingWord: true, grid: true },
};

function PostNotes({ card, look }: { card: SocialPostPreviewCardMetadata; look: LookSpec }) {
  const { i18n } = useTranslation();
  const scheduled = look.timingWord ? undefined : postScheduledLine(card, i18n.language);
  const expires = postExpiryNote(card);
  const notShown = (look.grid ? postGridShowsNotShown(card) : postShowsNotShown(card)) ? card.labels.mediaNotShown : undefined;
  if (!scheduled && !expires && !notShown) {
    return null;
  }
  return (
    <div className={styles.notes}>
      {scheduled && (
        <p className={styles.note} data-testid="social-post-scheduled">
          {scheduled}
        </p>
      )}
      {expires && (
        <p className={styles.note} data-testid="social-post-expires">
          {expires}
        </p>
      )}
      {notShown && (
        <p className={styles.note} data-testid="post-not-shown">
          {notShown}
        </p>
      )}
    </div>
  );
}

/**
 * The held/previewed social post. `full` (the default) is the network-true look the chat and the task screens
 * draw. `compact` is the island quick-view's form: a thumbnail, the account, and two lines of caption, with no
 * control of its own, so the whole preview taps through to the card that holds it (the island opens the task).
 */
export function SocialPostPreviewCard({ card, variant = 'full' }: { card: SocialPostPreviewCardMetadata; variant?: 'full' | 'compact' }) {
  const seenRef = useItemSeenBi('social_post_preview_card', {
    additionalFields: {
      network: card.network,
      media: String(card.mediaCount),
      scheduled: String(Boolean(card.scheduledAt || card.scheduledText)),
    },
  });
  return variant === 'compact' ? <CompactPost card={card} seenRef={seenRef} /> : <FullPost card={card} seenRef={seenRef} />;
}

/** The compact thumbnail rendition: a 56px tile at 2x. */
const COMPACT_RENDITION: SocialPostRendition = { mode: 'fill', width: 112, height: 112 };

function CompactPost({ card, seenRef }: { card: SocialPostPreviewCardMetadata; seenRef: ReturnType<typeof useItemSeenBi> }) {
  const [failed, setFailed] = useState(false);
  const view = compactPostPreview(card);
  return (
    <div ref={seenRef} className={compactStyles.root} data-testid="social-post-preview-compact" data-network={card.network}>
      {view.thumb && !failed && (
        <span className={compactStyles.thumb} data-kind={view.thumb.kind}>
          {view.thumb.kind === 'video' ? (
            <>
              <video className={compactStyles.media} src={socialPostVideoSrc(view.thumb.url)} preload="metadata" muted playsInline onError={() => setFailed(true)} />
              <span className={`${styles.play} ${styles.playSmall}`}>
                <Play size={13} fill="currentColor" strokeWidth={0} aria-hidden="true" />
              </span>
            </>
          ) : (
            <img className={compactStyles.media} src={socialPostImageRendition(view.thumb.url, COMPACT_RENDITION)} alt="" loading="lazy" onError={() => setFailed(true)} />
          )}
        </span>
      )}
      <span className={compactStyles.body}>
        <span className={compactStyles.head}>
          <SocialLogo network={socialPostLogoKey(card.network)} size={14} />
          <bdi className={compactStyles.title}>{view.title}</bdi>
        </span>
        {view.caption && (
          <p className={compactStyles.caption} dir={postCaptionDirection(view.caption) ?? 'auto'} data-testid="social-post-compact-caption">
            {view.caption}
          </p>
        )}
      </span>
    </div>
  );
}

function FullPost({ card, seenRef }: { card: SocialPostPreviewCardMetadata; seenRef: ReturnType<typeof useItemSeenBi> }) {
  const { logPageClickBi } = useGenericBi();
  const [portrait, setPortrait] = useState(false);
  const look = socialPostLook(card);
  const drawn = look ? LOOKS[look] : undefined;
  const Body = drawn?.body ?? GenericPostBody;
  const openMedia = (media: SocialPostPreviewMedia, index: number) => {
    logPageClickBi({ page_name: 'chat', button_name: 'social_post_preview_media', answer: media.kind });
    if (openMediaCollection(card.media.map((item, at) => ({ ...item, name: socialPostMediaName(item, at) })), index,
      { pageName: 'document_viewer', besideChat: true })) return;
    openDocViewer({
      file: { name: socialPostMediaName(media, index), isApp: false, content: media.url },
      kind: media.kind,
      pageName: 'document_viewer',
      besideChat: true,
    });
  };
  const logCaptionToggle = (expanded: boolean) =>
    logPageClickBi({ page_name: 'chat', button_name: expanded ? 'social_post_preview_see_more' : 'social_post_preview_see_less' });
  const logLink = () => logPageClickBi({ page_name: 'chat', button_name: 'social_post_preview_link' });
  const logControl = (control: PostControl) => logPageClickBi({ page_name: 'chat', button_name: control });
  const cardClass = [styles.card, drawn?.card, portrait && drawn?.portrait].filter(Boolean).join(' ');
  const body = (
    <PostControlContext.Provider value={logControl}>
      <Body
        card={card}
        onOpenMedia={openMedia}
        onToggleCaption={logCaptionToggle}
        onOpenLink={logLink}
        onPortrait={drawn?.portrait ? setPortrait : undefined}
      />
    </PostControlContext.Provider>
  );

  return (
    <div ref={seenRef} className={styles.root} data-testid="chat-social-post-preview-card" data-network={card.network}>
      {drawn && !drawn.card ? (
        <article className={`${styles.bare} ${drawn.frame}`} aria-label={card.networkLabel} data-post-look={look}>
          {body}
        </article>
      ) : (
        <Card component="article" variant="sunken" disablePadding className={cardClass} aria-label={card.networkLabel} data-post-look={look}>
          {body}
        </Card>
      )}
      <PostNotes card={card} look={drawn ?? GENERIC} />
    </div>
  );
}

const META_CLASSES: PostSubtitleClasses = { part: styles.metaPart, pause: styles.srOnly };

function GenericPostBody({ card, onOpenMedia: openMedia, onToggleCaption, onOpenLink }: SocialPostLookProps) {
  const { t } = useTranslation();
  const header = socialPostHeader(card);
  const layout = socialPostMediaLayout(card);
  const host = card.link && isSafeUrl(card.link) ? socialPostLinkHost(card.link) : undefined;
  const tileLabel = (media: SocialPostPreviewMedia) => (media.kind === 'video' ? t('docViewer.audioPlay') : t('files.type.image'));

  return (
    <>
      <header className={styles.head}>
        <AccountMark network={card.network} pictureUrl={card.accountPictureUrl} />
        <div className={styles.who}>
          <span className={styles.title} data-testid="social-post-title">
            <bdi>{header.title}</bdi>
          </span>
          {header.meta && (
            <span className={styles.meta} data-testid="social-post-meta">
              <PostSubtitleParts parts={header.meta} classes={META_CLASSES} />
            </span>
          )}
        </div>
      </header>
      {card.caption && <Caption text={card.caption} seeMore={card.labels.seeMore} seeLess={card.labels.seeLess} onToggle={onToggleCaption} />}
      {layout.tiles.length > 0 && (
        <div className={styles.mediaZone}>
          {layout.tiles.length === 1 && (
            <SingleTile
              key={layout.tiles[0].url}
              media={layout.tiles[0]}
              postType={card.postType}
              label={tileLabel(layout.tiles[0])}
              onOpen={() => openMedia(layout.tiles[0], 0)}
            />
          )}
          {layout.tiles.length > 1 && (
            <div
              className={`${styles.grid} ${GRID_CLASS[layout.tiles.length]}`}
              data-testid="social-post-media-grid"
              data-count={layout.tiles.length}
            >
              {layout.tiles.map((media, index) => (
                <MediaTile
                  key={`${media.url}-${index}`}
                  media={media}
                  label={tileLabel(media)}
                  rendition={socialPostGridRendition(layout.tiles.length, index)}
                  large={layout.tiles.length === 3 && index === 0}
                  more={index === layout.tiles.length - 1 ? layout.more : 0}
                  onOpen={() => openMedia(media, index)}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {host && (
        <a className={styles.link} href={card.link} target="_blank" rel="noopener noreferrer" dir="ltr" onClick={onOpenLink}>
          <Link size={16} aria-hidden="true" className={styles.linkIcon} />
          <span className={styles.host}>{host}</span>
        </a>
      )}
    </>
  );
}

function AccountMark({ network, pictureUrl }: { network: string; pictureUrl?: string }) {
  const [failed, setFailed] = useState(false);
  const logo = socialPostLogoKey(network);
  const known = socialPostHasLogo(network);
  if (pictureUrl && !failed) {
    return (
      <span className={styles.mark} data-testid="social-post-account-picture">
        <Avatar initials="" photoUrl={pictureUrl} onPhotoError={() => setFailed(true)} size={36} color={AVATAR_PALETTE[0]} />
        {known && (
          <span className={styles.brandBadge} data-testid="social-post-brand-badge">
            <SocialLogo network={logo} size={12} />
          </span>
        )}
      </span>
    );
  }
  return (
    <span className={`${styles.mark} ${styles.markTile}`} data-testid="social-post-network-mark" data-known={String(known)}>
      <SocialLogo network={logo} size={18} />
    </span>
  );
}

function SingleTile({
  media,
  postType,
  label,
  onOpen,
}: {
  media: SocialPostPreviewMedia;
  postType: SocialPostPreviewCardMetadata['postType'];
  label: string;
  onOpen: () => void;
}) {
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const frame = socialPostSingleFrame(media.kind === 'video' ? socialPostVideoAspect(postType) : aspect);
  return (
    <MediaTile
      media={media}
      label={label}
      rendition={SOCIAL_POST_RENDITIONS.single}
      large
      more={0}
      className={frame.portrait ? styles.portrait : styles.landscape}
      style={{ aspectRatio: String(frame.aspectRatio) } as CSSProperties}
      onMeasure={media.kind === 'image' ? setAspect : undefined}
      onOpen={onOpen}
    />
  );
}

function MediaTile({
  media,
  label,
  large,
  more,
  rendition,
  className,
  style,
  onMeasure,
  onFail,
  onOpen,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  rendition: SocialPostRendition;
  large: boolean;
  more: number;
  className?: string;
  style?: CSSProperties;
  onMeasure?: (aspect: number | undefined) => void;
  onFail?: () => void;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const fail = () => {
    setFailed(true);
    onMeasure?.(undefined);
    onFail?.();
  };
  const measure = (width: number, height: number) => onMeasure?.(width > 0 && height > 0 ? width / height : undefined);
  return (
    <button
      type="button"
      className={[styles.tile, large && styles.lead, className].filter(Boolean).join(' ')}
      style={style}
      aria-label={label}
      data-kind={media.kind}
      onClick={onOpen}
    >
      {failed ? (
        <span className={styles.failed} data-testid="social-post-media-placeholder">
          {more === 0 && <ImageOff size={20} aria-hidden="true" />}
        </span>
      ) : media.kind === 'video' ? (
        <video
          className={styles.media}
          src={socialPostVideoSrc(media.url)}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={(event) => measure(event.currentTarget.videoWidth, event.currentTarget.videoHeight)}
          onError={fail}
        />
      ) : (
        <img
          className={styles.media}
          src={socialPostImageRendition(media.url, rendition)}
          alt=""
          loading="lazy"
          onLoad={(event) => measure(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)}
          onError={fail}
        />
      )}
      {media.kind === 'video' && !failed && more === 0 && (
        <span className={`${styles.play} ${large ? styles.playLarge : styles.playSmall}`} data-testid="social-post-play">
          <Play size={large ? 18 : 13} fill="currentColor" strokeWidth={0} aria-hidden="true" />
        </span>
      )}
      {more > 0 && (
        <span className={styles.more} data-testid="social-post-more">
          +{more}
        </span>
      )}
    </button>
  );
}

function Caption({ text, seeMore, seeLess, onToggle }: { text: string; seeMore: string; seeLess: string; onToggle: (expanded: boolean) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(() => socialPostCaptionIsLong(text));
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || expanded || node.clientHeight === 0) {
      return;
    }
    setOverflowing(node.scrollHeight > node.clientHeight + 1);
  }, [text, expanded]);

  return (
    <div className={styles.captionZone}>
      <p ref={ref} className={expanded ? styles.caption : `${styles.caption} ${styles.clamped}`} data-testid="social-post-caption">
        {text.split('\n').map((paragraph, at) => (
          <Fragment key={at}>
            {at > 0 && '\n'}
            {paragraph && (
              <span dir={postCaptionDirection(paragraph) ?? 'auto'} data-testid="social-post-caption-text">
                {socialPostCaptionSegments(paragraph).map((segment, index) =>
                  segment.highlighted ? (
                    <span key={index} className={styles.tag} dir="auto">
                      {segment.text}
                    </span>
                  ) : (
                    segment.text
                  ),
                )}
              </span>
            )}
          </Fragment>
        ))}
      </p>
      {(expanded || overflowing) && (
        <TextButton
          size="small"
          className={styles.toggle}
          aria-expanded={expanded}
          onClick={() => {
            setExpanded(!expanded);
            onToggle(!expanded);
          }}
        >
          {expanded ? seeLess : seeMore}
        </TextButton>
      )}
    </div>
  );
}
